/**
 * Hook qui applique le thème (light / dark / system) sur <html>.
 *
 * - Positionne l'attribut `data-theme` sur le document.
 * - Écoute `prefers-color-scheme` quand le choix est "system".
 * - Met à jour la balise <meta name="theme-color"> pour l'UI du navigateur.
 */

import { useEffect, useState } from 'react';
import type { ThemePreference } from '../types/app';
import { applyStatusBarFromTheme } from '../platform/capacitor';

const THEME_COLORS = { dark: '#0b141a', light: '#ffffff' } as const;

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function applyTheme(resolved: 'light' | 'dark') {
  document.documentElement.setAttribute('data-theme', resolved);

  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (meta) meta.content = THEME_COLORS[resolved];

  applyStatusBarFromTheme();
}

export function useTheme(preference: ThemePreference) {
  useEffect(() => {
    if (preference !== 'system') {
      applyTheme(preference);
      return;
    }

    applyTheme(getSystemTheme());

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme(getSystemTheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);
}

/** Returns the resolved theme ('light' | 'dark'), reactive to system changes. */
export function useResolvedTheme(preference: ThemePreference): 'light' | 'dark' {
  const [resolved, setResolved] = useState<'light' | 'dark'>(
    preference !== 'system' ? preference : getSystemTheme(),
  );

  useEffect(() => {
    if (preference !== 'system') {
      setResolved(preference);
      return;
    }
    setResolved(getSystemTheme());
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setResolved(getSystemTheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  return resolved;
}
